import { userAtom } from '@/global-store';
import useFormSchema from '@/hooks/useFormSchema';
import {
  Alert,
  ComboboxCollection,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxTriggerButton,
  ComboboxValue,
  ComboboxLabel,
} from '@eye-able/ui';
import PhoneInput from '@components/PhoneInput.jsx';
import { useNotification } from '@eye-able/ui';
import {
  useCreateUser,
  useFetchOrganisations,
  usePostOrganisationAdmin,
  useUpdateUser,
} from '@http/queries.js';
import { Button, Collapse, MenuItem, Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { IconEdit, IconUserPlus, IconUsersGroup } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { Select, Switches, TextField } from 'mui-rff';
import nanoclone from 'nanoclone';
import { Field, Form } from 'react-final-form';
import * as yup from 'yup';
import DynamicDialog from './DynamicDialog';
import { noLeadingOrTrailingSpaces } from '@/helpers/yupValidation.js';
import OrgDialog from '@/components/OrgDialog';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';
import { T } from '@/i18n/T';

export default function UserDialog({
  userData,
  onSuccess,
  useOrgFetch = () => useFetchOrganisations('all'),
  useOrgPost = usePostOrganisationAdmin,
  getOrgOptionDisabled,
  hideOrgAdminToggle = false,
  showOrgSelect = false,
  hintText,
  ...dialogProps
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState(null);

  const { i18n, t } = useInlineTranslation();
  const notification = useNotification();
  const [currentUser] = useAtom(userAtom);

  const createUserMutation = useCreateUser();
  const editUserMutation = useUpdateUser();

  const { data: orgList, isLoading } = useOrgFetch();

  const languages = [
    {
      label: t('de'),
      value: 'de',
    },
    {
      label: t('en'),
      value: 'en',
    },
  ];

  let editUser = !!userData;

  const defaultValues = {
    role: 'customer',
    mailLang: i18n.resolvedLanguage === 'de' ? 'de' : 'en',
    sendSetupMail: !editUser,
    primaryOrganisation: showOrgSelect ? null : currentUser.primaryOrganisation,
    orgAdmin:
      userData?.orgAdmin || currentUser.isAdmin || currentUser.isSalesAdmin,
    firstName: '',
    lastName: '',
    company: showOrgSelect ? null : currentUser.company,
    email: '',
    phone: '',
  };

  if (!userData) {
    userData = defaultValues;
  }

  const initialValues = {
    ...defaultValues,
    ...userData,
  };

  const showRestrictedLicensePromotionWarning =
    editUser && Boolean(userData.constraintLicenses) && !userData.orgAdmin;

  const { validate, required } = useFormSchema(
    yup.object({
      firstName: noLeadingOrTrailingSpaces('First Name').max(128).required(),
      lastName: noLeadingOrTrailingSpaces('Last Name').max(128).required(),
      email: yup.string().max(128).email().required(),
      mailLang: yup.string().required().default('de'),
      sendSetupMail: yup.boolean().default(false),
      role: yup
        .string()
        .oneOf(['customer', 'partner', 'admin', 'publicAdmin', 'salesAdmin'])
        .default('customer'),
      primaryOrganisation:
        userData.primaryOrganisation || !editUser
          ? yup.number().min(1).required()
          : yup.number().min(1).nullable(),
      orgAdmin: yup
        .boolean()
        .default(
          userData.orgAdmin || currentUser.isAdmin || currentUser.isSalesAdmin,
        ),
    }),
  );

  async function createUser(body, form) {
    try {
      const res = await createUserMutation.mutateAsync(body);

      const message = t('userCreatedSuccess');
      notification.success(message, { duration: 15000 });

      form.restart();
      setIsDialogOpen(false);

      onSuccess?.(res);
      return true;
    } catch (error) {
      let message = t('userCreatedFailed');

      if (error?.message?.includes('Email already')) {
        message = t('mailAlreadyExists');
      }

      notification.error(message, { duration: 15000 });
      console.error(message + '\n\n' + JSON.stringify(error, null, 2));
    }
  }

  async function updateUser(body, form) {
    try {
      const res = await editUserMutation.mutateAsync({
        modifyUserApiKey: userData.apiKey,
        ...body,
      });

      const message = t('userUpdatedSuccess');
      notification.success(message, { duration: 15000 });

      form.restart();
      setIsDialogOpen(false);

      onSuccess?.(res);
      return true;
    } catch (error) {
      const message = t('userUpdatedFailed');

      notification.error(message, { duration: 15000 });
      console.error(message + '\n\n' + JSON.stringify(error, null, 2));
    }
  }

  function submit(values, form) {
    console.log('submit', values);
    const body = { ...userData, ...values };

    body.company = body.primaryOrganisationObject?.name || null;
    delete body.fullName;

    if (editUser) {
      return updateUser(body, form);
    } else {
      return createUser(body, form);
    }
  }

  function handleCreateOrganization(inputValue) {
    setNewOrgName(inputValue);
    setIsDialogOpen(false);
    setIsOrgDialogOpen(true);
  }

  function handleOrgCreationClose() {
    setNewOrgName(null);
    setIsOrgDialogOpen(false);
    setIsDialogOpen(true);
  }

  return (
    <Form
      validate={validate}
      key={`${editUser}-${isLoading}`}
      initialValues={initialValues}
      onSubmit={submit}
      render={({ handleSubmit, form, values }) => (
        <form noValidate onSubmit={handleSubmit}>
          <OrgControlledDialog
            form={form}
            isOpen={isOrgDialogOpen}
            onClose={handleOrgCreationClose}
            usePost={useOrgPost}
            name={newOrgName}
          />
          <DynamicDialog
            onConfirm={handleSubmit}
            title={editUser ? <T k='editUser' /> : <T k='addUser' />}
            activatorText={editUser ? <T k='editUser' /> : <T k='addUser' />}
            activatorProps={{
              color: 'primary',
              variant: 'contained',
              startIcon: editUser ? <IconEdit /> : <IconUserPlus />,
            }}
            overwriteOpen={isDialogOpen}
            onOpen={() => setIsDialogOpen(true)}
            onCancel={() => setIsDialogOpen(false)}
            fullWidth
            {...dialogProps}
          >
            <Grid
              container
              sx={{
                alignItems: 'center',
                gap: 2.5,
                justifyContent: 'center',
                my: 2.5,
                px: { xs: 3, md: 4, xl: 5.5 },
              }}
            >
              {showRestrictedLicensePromotionWarning && values.orgAdmin ? (
                <Alert variant='warning'>
                  {t('orgAdminPromotionClearsRestrictedLicensesWarning')}
                </Alert>
              ) : null}
              {hintText && <p>{hintText}</p>}
              {showOrgSelect && (
                <OrgAutocomplete
                  form={form}
                  getOptionDisabled={getOrgOptionDisabled}
                  orgList={orgList ?? []}
                  onCreateOrganization={handleCreateOrganization}
                  required={required.primaryOrganisation}
                />
              )}
              <Collapse
                in={!showOrgSelect || editUser || values.primaryOrganisation}
                unmountOnExit
              >
                <Grid
                  container
                  sx={{
                    alignItems: 'center',
                    gap: 2.5,
                    justifyContent: 'center',
                  }}
                >
                  <TextField
                    autoComplete='new-password'
                    fullWidth
                    label={<T k='firstName' />}
                    name='firstName'
                    required={required.firstName}
                  />
                  <TextField
                    autoComplete='new-password'
                    fullWidth
                    label={<T k='lastName' />}
                    name='lastName'
                    required={required.lastName}
                  />
                  <TextField
                    autoComplete='new-password'
                    fullWidth
                    label='Email'
                    name='email'
                    required={required.email}
                  />
                  <PhoneInput
                    name='phone'
                    fieldProps={{ parse: (value) => value || '' }}
                  />
                  {/* <CountrySelect /> */}

                  <Select
                    formControlProps={{ fullWidth: true }}
                    fullWidth
                    label={<T k='mailLang' />}
                    name='mailLang'
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.value} value={lang.value}>
                        {lang.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {currentUser.isAdmin && userData.id !== currentUser.id && (
                    <TextField
                      autoComplete='new-password'
                      fullWidth
                      label={<T k='role' />}
                      name='role'
                      required={required.role}
                      select
                    >
                      <MenuItem value='customer'>Customer</MenuItem>

                      <MenuItem value='publicAdmin'>PublicAdmin</MenuItem>

                      <MenuItem value='partner'>Partner</MenuItem>

                      <MenuItem value='salesAdmin'>SalesAdmin</MenuItem>

                      <MenuItem value='admin'>ADMIN</MenuItem>
                    </TextField>
                  )}
                  {!editUser &&
                    (currentUser.isAdmin || currentUser.isSalesAdmin) && (
                      <>
                        <Switches
                          name='sendSetupMail'
                          data={{
                            label: t('sendMailToUser'),
                            value: false,
                          }}
                        />
                      </>
                    )}
                  {editUser &&
                    userData.id !== currentUser.id &&
                    (currentUser.isAdmin || currentUser.isOrgAdmin) && (
                      <Switches
                        name='isActive'
                        data={{
                          label: `${t('isActive')}?`,
                          value: userData.isActive,
                        }}
                      />
                    )}
                  {!hideOrgAdminToggle &&
                    (currentUser.isAdmin || currentUser.isOrgAdmin) && (
                      <Switches
                        name='orgAdmin'
                        data={{
                          label: (
                            <Tooltip title={t('orgAdminDescription')}>
                              <span>
                                <T k='orgAdmin' />?
                              </span>
                            </Tooltip>
                          ),
                          value:
                            userData.orgAdmin ||
                            currentUser.isAdmin ||
                            currentUser.isSalesAdmin,
                        }}
                      />
                    )}
                </Grid>
              </Collapse>
            </Grid>
          </DynamicDialog>
        </form>
      )}
    />
  );
}

function OrgControlledDialog({ form, isOpen, onClose, usePost, name }) {
  function handleOrgCreationSuccess(newOrg, form) {
    const currentFormValues = nanoclone(form.getState().values);

    form.reset();

    setTimeout(() => {
      form.batch(() => {
        if (currentFormValues.firstName) {
          form.change('firstName', currentFormValues.firstName);
        }
        if (currentFormValues.lastName) {
          form.change('lastName', currentFormValues.lastName);
        }
        if (currentFormValues.email) {
          form.change('email', currentFormValues.email);
        }
        if (currentFormValues.phone) {
          form.change('phone', currentFormValues.phone);
        }
        if (currentFormValues.role) {
          form.change('role', currentFormValues.role);
        }
        if (currentFormValues.mailLang) {
          form.change('mailLang', currentFormValues.mailLang);
        }

        form.change('primaryOrganisation', newOrg.id);
      });
    }, 350);

    onClose();
  }

  return (
    <OrgDialog
      usePost={usePost}
      onSuccess={(newOrg) => handleOrgCreationSuccess(newOrg, form)}
      hideActivator
      overwriteOpen={isOpen}
      onCancel={onClose}
      initialValues={{ name }}
    />
  );
}

function OrgAutocomplete({
  getOptionDisabled,
  orgList,
  form,
  onCreateOrganization,
  required,
}) {
  const { t } = useInlineTranslation();
  const [inputValue, setInputValue] = useState('');

  const DEFAULT_VISIBLE_ITEMS = 10;
  const MAX_SEARCH_RESULTS = 50;

  const { items, hasMore } = useMemo(() => {
    const safe = orgList || [];
    const trimmed = inputValue.trim();
    const lower = trimmed.toLowerCase();

    let filtered;
    let hasMore = false;
    if (lower) {
      filtered = [];
      for (let i = 0; i < safe.length; i++) {
        if (safe[i].name?.toLowerCase()?.includes(lower)) {
          if (filtered.length >= MAX_SEARCH_RESULTS) {
            hasMore = true;
            break;
          }
          filtered.push(safe[i]);
        }
      }
    } else {
      filtered = safe.slice(0, DEFAULT_VISIBLE_ITEMS);
      hasMore = safe.length > DEFAULT_VISIBLE_ITEMS;
    }

    const exactMatch = lower
      ? safe.find((o) => o.name?.toLowerCase() === lower)
      : null;

    if (trimmed && !exactMatch) {
      filtered.push({
        id: 'add',
        name: trimmed,
        isCreateOption: true,
        inputValue: trimmed,
      });
    }

    return { items: filtered, hasMore };
  }, [orgList, inputValue]);

  return (
    <Field name='primaryOrganisation'>
      {({ input }) => {
        const selectedOrg =
          (orgList || []).find((o) => o.id === input.value) || null;

        return (
          <ComboboxRoot
            items={items}
            value={selectedOrg}
            inputValue={inputValue}
            onInputValueChange={setInputValue}
            onValueChange={(newValue) => {
              if (newValue?.isCreateOption) {
                onCreateOrganization(newValue.inputValue);
                return;
              }
              form.change('primaryOrganisation', newValue?.id);
            }}
            itemToStringLabel={(item) => item?.name || ''}
            itemToStringValue={(item) => String(item?.id ?? '')}
            autoHighlight
          >
            <div className='eye:group/combobox-field eye:block eye:w-full'>
              <ComboboxTrigger>
                <ComboboxLabel placement='inside'>
                  {required ? `${t('organisation')} *` : t('organisation')}
                </ComboboxLabel>
                <ComboboxTriggerButton aria-required={required}>
                  <ComboboxValue>
                    {(value) => (
                      <span
                        className={
                          value
                            ? 'eye:truncate'
                            : 'eye:truncate eye:text-grey-placeholder'
                        }
                      >
                        {value?.name || t('selectOrCreateOrganisation')}
                      </span>
                    )}
                  </ComboboxValue>
                </ComboboxTriggerButton>
              </ComboboxTrigger>
            </div>
            <ComboboxContent>
              <ComboboxInput />
              <ComboboxList>
                <ComboboxCollection>
                  {(item) => {
                    if (item.isCreateOption) {
                      return (
                        <ComboboxItem key='add-organization' value={item}>
                          <Button
                            fullWidth
                            startIcon={<IconUsersGroup />}
                            component='span'
                          >
                            <T
                              k='createOrgNamed'
                              options={{ name: item.inputValue }}
                            />
                          </Button>
                        </ComboboxItem>
                      );
                    }

                    const disabled = getOptionDisabled?.(item);

                    return (
                      <ComboboxItem
                        key={item.id}
                        value={item}
                        disabled={disabled}
                      >
                        {item.name}
                      </ComboboxItem>
                    );
                  }}
                </ComboboxCollection>
                {hasMore && (
                  <div
                    aria-disabled='true'
                    className='eye:px-16 eye:py-2 eye:text-sm eye:text-grey-placeholder eye:italic'
                  >
                    ...use the search to load more results
                  </div>
                )}
              </ComboboxList>
            </ComboboxContent>
          </ComboboxRoot>
        );
      }}
    </Field>
  );
}
